import React from "react";
import styled from "styled-components";
import { useState, useEffect } from "react";
import { fmtPrice, fmtCompactUsd, fmtChange } from "./utils";
import { getWatchlistApi } from "./getWatchlistApi";
import Seo from "./Seo";
import { routeMeta } from "./routeMeta";
import PageHeading from "./PageHeading";
import { PageContainer } from "./sharedStyleds";

// Below this width the table drops to three columns - coin, price, 24h change -
// and folds volume under the price. Six columns needed 700px, so on a phone the
// table used to scroll sideways, and the change columns, which are what most
// people come to the page for, started off-screen.
const NARROW = "650px";

// The wrapper scrolls, not the page. The whole page used to be the scroll
// container, and on mount it scrolled itself right by the table's offsetLeft -
// which is exactly the page padding - so the heading and intro sat flush against
// the left edge of the screen while every other page kept its margin. Tablets
// between the narrow layout and 700px still get a scrollbar here if they need it.
const TableScroll = styled.div`
  overflow-x: auto;
`;

const WatchlistTable = styled.table`
  width: 100%;
  min-width: 700px;
  margin: 0;
  border-collapse: collapse;

  td,
  th {
    border: 1px solid var(--code-bg-color);
    padding: 0.55rem 10px;
    vertical-align: middle;
  }

  th {
    font-size: 1rem;
    text-align: left;
    white-space: nowrap;
  }

  @media (max-width: ${NARROW}) {
    min-width: 0;

    td,
    th {
      padding: 0.5rem 6px;
    }

    .wide {
      display: none;
    }
  }
`;

const Coin = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Image = styled.img`
  width: 36px;
  height: 36px;
  flex-shrink: 0;

  @media (max-width: ${NARROW}) {
    width: 28px;
    height: 28px;
  }
`;

const Ticker = styled.div`
  font-weight: 700;
  line-height: 1.2;
`;

const FullName = styled.div`
  font-size: 0.8rem;
  font-weight: 100;
  color: var(--secondary-color);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 14ch;
`;

const NumCell = styled.td`
  font-weight: 100;
  color: var(--secondary-color);
  white-space: nowrap;
  text-align: right;
`;

// Volume folded under the price on narrow screens only, so a phone still shows
// what the list is ranked by.
const NarrowVolume = styled.div`
  display: none;
  font-size: 0.75rem;
  color: #a3abba;

  @media (max-width: ${NARROW}) {
    display: block;
  }
`;

// The same up and down colours as the whale panel's exchange flow, rather than
// the pure red and green this used before. Coloured by the rounded figure, not
// the raw one, so -0.004% - which reads "0.00%" - is not painted as a loss.
const Change = styled(NumCell)`
  color: ${({ $value }) => {
    const hundredths = Math.round(($value ?? 0) * 100);
    if (hundredths > 0) return "#26a65b";
    if (hundredths < 0) return "#ff3c74";
    return "var(--secondary-color)";
  }};
`;

// Spans the whole table so the message sits under the headers rather than
// squeezing itself into the first column's width.
const StatusCell = styled.td`
  padding: 1rem 10px;
  font-weight: 100;
  color: var(--secondary-color);
`;

// Matches the source note on /mempool - same size and colour, so the two pages
// credit their data the same way.
const SourceNote = styled.p`
  font-size: 0.75rem;
  color: #a3abba;
  margin-top: 0.6rem;
  max-width: 70ch;
`;

// Matched to the cache window in netlify/functions/watchlist.mjs. Polling faster
// than the cache refreshes asks the CDN the same question repeatedly for one new
// answer - it was 10 seconds once, which is what exhausted the old provider's
// quota. The source note below renders this value, so the interval and the
// sentence cannot drift apart.
const REFRESH_MS = 5 * 60 * 1000;

const Watchlist = () => {
  // null until the first answer arrives, so "loading" and "there is genuinely
  // nothing" can say different things instead of both rendering bare headers.
  const [cryptoData, setCryptoData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const doFetch = async () => {
      const coins = await getWatchlistApi();

      // A failed refresh leaves the rows that are already on screen alone.
      // Overwriting good prices with an empty table is strictly worse than
      // showing prices that are a minute or two old.
      setFailed(coins === null);
      if (coins !== null) setCryptoData(coins);
    };

    doFetch();

    const intervalId = setInterval(doFetch, REFRESH_MS);

    return () => clearInterval(intervalId);
  }, []);

  const rows = cryptoData ?? [];

  // Three distinct situations that all used to render as an empty table body:
  // nothing has come back yet, something came back but it was an error, and the
  // request succeeded and the list really is empty.
  let statusMessage = "No coins to show right now.";
  if (failed) {
    statusMessage = "Prices are temporarily unavailable. Trying again shortly.";
  } else if (cryptoData === null) {
    statusMessage = "Loading prices...";
  }

  return (
    <PageContainer>
      <Seo {...routeMeta["/watchlist"]} path="/watchlist" />
      <PageHeading meta={routeMeta["/watchlist"]} />
      <TableScroll>
        <WatchlistTable>
          <thead>
            <tr>
              <th>Coin</th>
              <th style={{ textAlign: "right" }}>Price</th>
              <th className="wide" style={{ textAlign: "right" }}>
                Volume 24h
              </th>
              <th className="wide" style={{ textAlign: "right" }}>
                Market cap
              </th>
              <th className="wide" style={{ textAlign: "right" }}>
                1h
              </th>
              <th style={{ textAlign: "right" }}>24h</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(
              ({
                symbol,
                price,
                fullName,
                volume24h,
                marketCap,
                change1h,
                change24h,
                imageUrl,
              }) => (
                <tr key={symbol}>
                  <td>
                    <Coin>
                      <Image src={imageUrl} alt="" />
                      <div style={{ minWidth: 0 }}>
                        <Ticker>{symbol}</Ticker>
                        <FullName>{fullName}</FullName>
                      </div>
                    </Coin>
                  </td>
                  <NumCell>
                    {fmtPrice(price)}
                    <NarrowVolume>Vol {fmtCompactUsd(volume24h)}</NarrowVolume>
                  </NumCell>
                  <NumCell className="wide">{fmtCompactUsd(volume24h)}</NumCell>
                  <NumCell className="wide">{fmtCompactUsd(marketCap)}</NumCell>
                  <Change className="wide" $value={change1h}>
                    {fmtChange(change1h)}
                  </Change>
                  <Change $value={change24h}>{fmtChange(change24h)}</Change>
                </tr>
              )
            )}
            {rows.length === 0 && (
              <tr>
                <StatusCell colSpan={6}>{statusMessage}</StatusCell>
              </tr>
            )}
          </tbody>
        </WatchlistTable>
      </TableScroll>
      <SourceNote>
        Price data from{" "}
        <a href="https://www.coingecko.com" target="_blank" rel="noreferrer">
          CoinGecko
        </a>
        , refreshed every {REFRESH_MS / 60000} minutes.
      </SourceNote>
    </PageContainer>
  );
};

export default Watchlist;
